// Pure seat logic for sofas and beds (no Phaser), on the altana's model (altanaSeatsLogic.js): which spots a piece
// has, which are taken, which one to offer. A spot is named by a perch, "<placement id>/<seat>", and travels in
// presence as mode 'rest' + perch; the server keeps the first claim (backend app/realtime/perches.py).
import { FURNITURE_SPOTS, isSeatFurniture } from './furnitureSpots.mjs';

export const PERCH_RADIUS = 70;      // feet distance to a spot that offers "Usiądź" / "Połóż się"
export const TAP_SPOT_RADIUS = 45;   // display px round a free spot that a tap sits you on

const PERCH = /^([0-9a-f-]{36})\/([01])$/;

export const perchId = (placementId, seat) => `${placementId}/${seat}`;

export function parsePerch(s) {
  const m = typeof s === 'string' ? PERCH.exec(s) : null;
  return m ? { placementId: m[1], seat: Number(m[2]) } : null;
}

// box: the sprite's centre and display size. A flipped piece mirrors its spots about the centre, and the pose with it.
export function spotsFor(placement, box) {
  if (!isSeatFurniture(placement)) return [];
  const { pose, spots } = FURNITURE_SPOTS[placement.item];
  const flip = !!placement.flipped;
  return spots.map(({ u, v }, seat) => {
    const dx = (u - 0.5) * box.w;
    return { perch: perchId(placement.id, seat), x: box.x + (flip ? -dx : dx), y: box.y + (v - 0.5) * box.h, pose, flip, scale: box.scale ?? 1 };
  });
}

export function takenPerches(peers) {
  const taken = new Set();
  for (const p of peers) if (p?.mode === 'rest' && typeof p.perch === 'string') taken.add(p.perch);
  return taken;
}

export function nearestFreeSpot(feet, spots, taken, radius = PERCH_RADIUS) {
  let best = null, bestD = radius;
  for (const s of spots) {
    if (taken.has(s.perch)) continue;
    const d = Math.hypot(feet.x - s.x, feet.y - s.y);
    if (d <= bestD) { best = s; bestD = d; }
  }
  return best;
}

// The piece's menu ("Usiądź" after a tap on the sofa): the free spot on the side you tapped, at any distance — only
// across matters, a tap on the backrest still means the seat below it. null when every spot is taken.
export function tappedSpot(tap, spots, taken) {
  let best = null;
  for (const s of spots) {
    if (!taken.has(s.perch) && (!best || Math.abs(tap.x - s.x) < Math.abs(tap.x - best.x))) best = s;
  }
  return best;
}

// boxes: Map placementId -> {placement, box}. null when this client can't place the perch (the piece is gone, moved
// rooms, or isn't a seat): the peer is then drawn standing where they are.
export function perchAnchor(perch, boxes) {
  const p = parsePerch(perch);
  const entry = p && boxes.get(p.placementId);
  const spot = entry && spotsFor(entry.placement, entry.box)[p.seat];
  return spot ? { x: spot.x, y: spot.y, pose: spot.pose, flip: spot.flip, scale: spot.scale } : null;
}

// After the room's placements are drawn again: may whoever sits on this piece stay? Only when the piece is still
// there exactly as it was — moved, flipped, resized or gone, they stand up (their spot is somewhere else now).
// old / next: the piece's {placement, box} before and after (next undefined = gone).
export function refreshOutcome(old, next) {
  if (!old || !next) return 'stand';
  const a = old.box, b = next.box;
  const same = a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h
    && !!old.placement.flipped === !!next.placement.flipped && old.placement.item === next.placement.item;
  return same ? 'keep' : 'stand';
}

export const SIT_LABEL = { sit: 'Usiądź', lie: 'Połóż się' };
export const STAND_LABEL = 'Wstań';
export const STAND_GAP = 10;   // standing up puts the feet this far below the piece's bottom edge

// Where standing up puts you: the spot's x, the feet just below the piece (box: its {x, y, w, h}, centre-anchored;
// null when the piece is gone — then just below the spot).
export function standFeet(spot, box) {
  return { x: spot.x, feetY: box ? box.y + box.h / 2 + STAND_GAP : spot.y + STAND_GAP };
}

// One frame of the action button. It is shared — placing furniture on a phone ("Przesuń tutaj") and the altana (with
// these same labels) use it too — so the seats replace or clear only the action they registered themselves (`mine`,
// compared by identity). cur: the slot now; perched: you sit or lie; near: the free spot in reach ({perch, pose}) or
// null; offer: the perch of the "Usiądź" on offer. -> {op: 'keep' | 'register' | 'clear', label}
export function seatAction({ cur, mine, perched, near = null, offer = null }) {
  const free = !cur, own = !!cur && cur === mine;
  if (perched) return free || (own && cur.label !== STAND_LABEL) ? { op: 'register', label: STAND_LABEL } : { op: 'keep' };
  if (near) {
    const label = SIT_LABEL[near.pose];
    return free || (own && (cur.label !== label || offer !== near.perch)) ? { op: 'register', label } : { op: 'keep' };
  }
  return own ? { op: 'clear' } : { op: 'keep' };
}
